import React, { PropsWithChildren, useEffect, useRef } from 'react';

import { isNotNull } from '../../../lib/typeGuards';
import useScrollAreaContext from './ScrollAreaContext';

type ScrollAreaProps = PropsWithChildren<{
  to: number;
  bottomOffset: number;
}>;

/**
 * Wraps an given Component into a scrollable view.
 */
function ScrollArea({ children, to = 80, bottomOffset = 0 }: ScrollAreaProps) {
  const wrapper = useRef<HTMLDivElement | null>(null);
  const { visible } = useScrollAreaContext();

  useEffect(() => {
    const { current } = wrapper;
    if (isNotNull(current)) {
      const resizeScrollArea = () => {
        // inside a PAL panel the scroll area must end above the viewport's bottom,
        // otherwise the widget's rounded corners and shadow would be clipped.
        // the height of the widget's bottom parts below the scroll area is defined
        // as the CSS variable --widget-rounded-corners-height so it can be adjusted
        // in the stylesheets (see _layout-variables.scss)
        const tabPanels = current.closest('.pal__tabpanels');
        if (tabPanels) {
          // the panel container can extend beyond the viewport (e.g. on small
          // screens), so anchor to the viewport but never below the panel's bottom
          const bottom = Math.min(window.innerHeight, tabPanels.getBoundingClientRect().bottom);
          // CSS custom properties are not resolved to pixels, so rem values are converted here
          const widget = current.closest('.widget');
          const roundedCornersHeight = parseFloat(
            widget ? getComputedStyle(widget).getPropertyValue('--widget-rounded-corners-height') : ''
          );
          const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          // fall back to the previous constant (in px) if the CSS variable is missing
          const bottomFrameHeight = roundedCornersHeight ? roundedCornersHeight * rootFontSize : 25;
          // calculation in pixels, but the output is converted to rem
          const height = bottom - current.getBoundingClientRect().top - bottomFrameHeight - bottomOffset;
          current.style.maxHeight = `${Math.max(0, height) / 16}rem`;
        } else {
          const height = (window.innerHeight / 100) * to - current.getBoundingClientRect().top - bottomOffset;
          const heightInRem = height ? Math.ceil(height / 16) : 0;
          current.style.maxHeight = `${heightInRem}rem`;
        }
      };

      // measure after the next frame so the layout has settled
      const frame = requestAnimationFrame(resizeScrollArea);

      // recalculate on viewport changes so the scroll area fits the current breakpoint
      window.addEventListener('resize', resizeScrollArea);

      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('resize', resizeScrollArea);
      };
    }
    // ignore warning: recalculate only if 'visible' changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <div ref={wrapper} className="scroll-area">
      {children}
    </div>
  );
}

export default ScrollArea;
